// The typed client of the cosmos API. No framework import: usable in Node and in the browser.
import createClient, { type Middleware } from "openapi-fetch";
import { isProblem, type Problem, ProblemError } from "./problem.js";
import type { paths } from "./schema.d.ts";

export * from "./knowledge-views.js";
export { isProblem, type Problem, ProblemError, type ProblemItem } from "./problem.js";
export * from "./refs.js";
export * from "./run-views.js";
export type { components, paths } from "./schema.d.ts";
export * from "./views.js";

export interface CosmosClientOptions {
  /** Origin of the cosmos API (for example http://127.0.0.1:8710), or "" for the same origin; paths carry /api/v1. */
  baseUrl: string;
  /** Returns the bearer token of each call, or undefined to send none (the platform's local caller). */
  token?: () => Promise<string | undefined> | string | undefined;
  fetch?: typeof fetch;
}

export type CosmosClient = ReturnType<typeof createClient<paths>>;

/** Builds a client whose every non-2xx answer is thrown as a ProblemError. */
export function createCosmosClient(options: CosmosClientOptions): CosmosClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl.replace(/\/$/, ""),
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(authentication(options.token));
  client.use(problems);
  return client;
}

function authentication(token: CosmosClientOptions["token"]): Middleware {
  return {
    async onRequest({ request }) {
      const value = await token?.();
      if (value) request.headers.set("Authorization", `Bearer ${value}`);
      return request;
    },
  };
}

const problems: Middleware = {
  async onResponse({ response }) {
    // An HTML page is never the API: a proxy or the web app's fallback answered in its place.
    if (response.ok && response.headers.get("content-type")?.includes("text/html")) {
      throw new ProblemError(
        {
          status: 502,
          title: "Bad Gateway",
          detail: "the cosmos API is not reachable at this address",
        },
        response.headers.get("x-request-id") ?? undefined,
      );
    }
    if (response.ok) return response;
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => undefined);
    const problem: Problem = isProblem(body)
      ? body
      : { status: response.status, title: response.statusText, detail: await response.text() };
    throw new ProblemError(problem, response.headers.get("x-request-id") ?? undefined);
  },
};
